import { forwardRef } from "react";

import { cx, styled } from "simplydo/react/styled";
import { computerUp, mobileOnly, tabletOnly } from "simplydo/react/theme";

import { textAlignCss } from "./Grid.styles";
import type { GridColumnProps } from "./Grid.types";

const StyledGridColumn = styled.div<{
  $span: number;
  $computer?: number;
  $tablet?: number;
  $mobile?: number;
  $textAlign?: string;
  $verticalAlign?: string;
  $stretched?: boolean;
}>`
  position: relative;
  box-sizing: border-box;
  min-width: 0;
  grid-column: span ${({ $span }) => $span};
  text-align: ${({ $textAlign }) => textAlignCss($textAlign)};
  align-self: ${({ $verticalAlign, $stretched }) => {
    if ($stretched) {
      return "stretch";
    }
    if ($verticalAlign === "top") {
      return "start";
    }
    if ($verticalAlign === "bottom") {
      return "end";
    }
    if ($verticalAlign === "middle") {
      return "center";
    }
    return "stretch";
  }};

  ${mobileOnly} {
    &.is-mobile {
      grid-column: span ${({ $mobile }) => $mobile};
    }
  }

  ${tabletOnly} {
    &.is-tablet {
      grid-column: span ${({ $tablet }) => $tablet};
    }
  }

  ${computerUp} {
    &.is-computer {
      grid-column: span ${({ $computer }) => $computer};
    }
  }
`;

const GridColumnComponent = forwardRef<HTMLDivElement, GridColumnProps>(
  (props, ref) => {
    const {
      span = 1,
      computer,
      tablet,
      mobile,
      textAlign,
      verticalAlign,
      children,
      className,
      style,
      stretched,
      ...rest
    } = props;
    return (
      <StyledGridColumn
        ref={ref}
        className={cx(
          mobile != null && "is-mobile",
          tablet != null && "is-tablet",
          computer != null && "is-computer",
          className,
        )}
        style={style}
        $span={span}
        $computer={computer}
        $tablet={tablet}
        $mobile={mobile}
        $textAlign={textAlign}
        $verticalAlign={verticalAlign}
        $stretched={stretched}
        {...rest}
      >
        {children}
      </StyledGridColumn>
    );
  },
);

GridColumnComponent.displayName = "GridColumn";

export const GridColumn = styled(GridColumnComponent)``;
